"use client";
import React from "react";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Save,
  Target,
  Settings,
  Plus,
  Minus,
  Edit3,
  Users,
  User,
  Search,
  Filter,
  ChevronRight,
  ChevronLeft,
  Pencil,
  X,
  Info,
  Check,
  Loader2,
} from "lucide-react";
import Modal from "../layout/Modal";
import "./TeamEditPage.css";
import { useNavigate, useParams } from "react-router-dom";
import { getAllPeopleApi } from "../../services/peopleService";
import { handleApiError as baseHandleApiError } from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import { getDepartmentsListApi } from "../../services/settingsService";
import {
  getTeamByIdApi,
  removeFromTeamApi,
  addToTeamApi,
  updateTeamApi,
  getSkillsByTeamDescApi,
} from "../../services/teamsService";
import NoDataFound from "../shared/NoDataFound";
import ViewMoreModal from "../shared/ViewMoreModal";

// Generic Modal for displaying skill lists
const SkillListModal = ({ title, skills, type, onClose, onRemoveSkill }) => {
  return (
    <div className="job-skills-modal-overlay">
      <div className="job-skills-modal-content unique-job-skills-modal">
        <div className="job-skills-modal-header">
          <h3>{title}</h3>
          <button onClick={onClose} className="job-skills-modal-close-btn">
            <X size={20} />
          </button>
        </div>
        <div className="job-skills-modal-grid">
          {type === "soft"
            ? skills.map((skill, index) => (
                <div key={index} className="soft-skill-item-modal">
                  <span className="soft-skill-name">{skill.name}:</span>
                  {skill.value !== undefined && (
                    <span className="soft-skill-value">{skill.value}</span>
                  )}
                </div>
              ))
            : skills.map((skill, index) => (
                <div key={index} className="job-skill-card">
                  <span>{skill}</span>
                  {onRemoveSkill && (
                    <button
                      onClick={() => onRemoveSkill(skill)}
                      className="job-skill-remove-btn"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
        </div>
      </div>
    </div>
  );
};

const TeamEditPage = ({ onSave }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
    action: null,
    showLocalButton: true,
  });
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [loadingTab, setLoadingTab] = useState(null); // 'skills' | null
  const [isUpdateLoading, setIsUpdateLoading] = useState(false); // for Next on members tab (update)

  const [activeTab, setActiveTab] = useState("basic");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("All");
  const [selectedMembers, setSelectedMembers] = useState([]);

  const [newSkill, setNewSkill] = useState(""); // This is for general team skills, not soft/job skills
  const [newNeededSkill, setNewNeededSkill] = useState("");
  const [newPositionType, setNewPositionType] = useState("");

  // New states for soft and job skills editing
  const [editingSoftSkill, setEditingSoftSkill] = useState(null); // { skillName: string, originalValue: number }
  const [softSkillValues, setSoftSkillValues] = useState({}); // To hold editable soft skill values
  const [tempSoftSkills, setTempSoftSkills] = useState({});
  const [tempJobSkills, setTempJobSkills] = useState([]);
  const [apiJobSkills, setApiJobSkills] = useState([]);
  const [removedJobSkills, setRemovedJobSkills] = useState([]); // array of lowercased labels

  // Modals for skill sections
  const [showSoftSkillListModal, setShowSoftSkillListModal] = useState({
    visible: false,
    title: "",
    skills: [],
    type: "soft",
  });
  const [showJobSkillListModal, setShowJobSkillListModal] = useState({
    visible: false,
    title: "",
    skills: [],
    type: "job",
    removable: false,
  });

  const departments = [
    "Engineering",
    "Product",
    "Analytics",
    "Sales",
    "Marketing",
    "Design",
  ];
  const workStyles = ["Remote", "Hybrid", "On-site", "Flexible"];

  // Common position types for dropdown
  const commonPositionTypes = [
    "Senior Frontend Developer",
    "Junior Frontend Developer",
    "Backend Developer",
    "Full Stack Developer",
    "UX Designer",
    "UI Designer",
    "Product Manager",
    "Data Scientist",
    "DevOps Engineer",
    "QA Engineer",
    "Technical Lead",
    "Engineering Manager",
  ];

  const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message || t("subscription.upgradePlanMessage"),
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  // Handlers for general team skills (not soft/job skills)
  const handleAddSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData({
        ...formData,
        skills: [...formData.skills, newSkill.trim()],
      });
      setNewSkill("");
    }
  };
  const Explainability = ({ text }) => {
    if (!text) return null;

    const normalized = text.replace(/\r\n/g, "\n").trim();
    const lines = normalized.split("\n").filter(Boolean);

    const heading = lines[0]; // first line is the sentence
    const bullets = lines
      .slice(1)
      .map((l) => l.replace(/^•\s*/, "").trim())
      .filter(Boolean);

    return (
      <>
        <p className="exp-heading">{heading}</p>
        {bullets.length > 0 && (
          <ul className="exp-list">
            {bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        )}
      </>
    );
  };

  const decideTooltipSide = (e) => {
    const wrapper = e.currentTarget.closest(".mobility-tooltip");
    if (!wrapper) return;

    const rect = wrapper.getBoundingClientRect();
    const tipWidth = 280; // keep in sync with CSS width
    const spaceRight = window.innerWidth - rect.right;
    const spaceLeft = rect.left;

    // If there's not enough space to the right, and more space on the left, flip it
    wrapper.dataset.side =
      spaceRight < tipWidth && spaceLeft > spaceRight ? "left" : "right";
  };

  const handleRemoveSkill = (skillToRemove) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter((skill) => skill !== skillToRemove),
    });
  };
  // const combinedJobSkills = useMemo(() => {
  //   const map = new Map(); // key(lowercased) -> { label, fromUser, fromApi }
  //   const add = (list, from) => {
  //     list.forEach((s) => {
  //       const raw = String(s || "").trim();
  //       if (!raw) return;
  //       const key = raw.toLowerCase();
  //       if (!map.has(key))
  //         map.set(key, { label: raw, fromUser: false, fromApi: false });
  //       const entry = map.get(key);
  //       entry[from] = true;
  //       // prefer user's exact casing/label if present
  //       if (from === "fromUser") entry.label = raw;
  //     });
  //   };
  //   add(apiJobSkills, "fromApi");
  //   add(tempJobSkills, "fromUser");
  //   return Array.from(map.values()); // [{label, fromUser, fromApi}]
  // }, [apiJobSkills, tempJobSkills]);
  const combinedJobSkills = useMemo(() => {
    const removed = new Set(removedJobSkills.map((s) => s.toLowerCase()));
    const map = new Map(); // key -> { label, fromUser, fromApi }

    const add = (list, from) => {
      (list || []).forEach((raw) => {
        const label = String(raw || "").trim();
        if (!label) return;
        const key = label.toLowerCase();
        if (removed.has(key)) return; // 👈 filter removed
        if (!map.has(key))
          map.set(key, { label, fromUser: false, fromApi: false });
        map.get(key)[from] = true;
        if (from === "fromUser") map.get(key).label = label; // prefer user casing
      });
    };

    add(apiJobSkills, "fromApi");
    add(tempJobSkills, "fromUser");

    return Array.from(map.values()); // [{label, fromUser, fromApi}]
  }, [apiJobSkills, tempJobSkills, removedJobSkills]);

  // Final needed skills array
  const finalNeededSkills = useMemo(
    () => combinedJobSkills.map((s) => s.label),
    [combinedJobSkills],
  );
  // const handleAddNeededSkill = () => {
  //   const s = (newNeededSkill || "").trim();
  //   if (!s) return;
  //   setTempJobSkills((prev) => {
  //     const lower = prev.map((x) => x.toLowerCase());
  //     if (lower.includes(s.toLowerCase())) return prev; // dedupe
  //     return [...prev, s];
  //   });
  //   setNewNeededSkill("");
  // };

  const handleAddNeededSkill = () => {
    const s = (newNeededSkill || "").trim();
    if (!s) return;
    setTempJobSkills((prev) => {
      const lower = prev.map((x) => x.toLowerCase());
      if (lower.includes(s.toLowerCase())) return prev;
      return [...prev, s];
    });
    // allow it back if it was previously removed
    setRemovedJobSkills((prev) => prev.filter((x) => x !== s.toLowerCase()));
    setNewNeededSkill("");
  };

  // const handleRemoveNeededSkill = (skillToRemove) => {
  //   const key = String(skillToRemove || "").toLowerCase();
  //   setTempJobSkills((prev) => prev.filter((s) => s.toLowerCase() !== key));
  // };

  const handleRemoveNeededSkill = (skillToRemove) => {
    const key = String(skillToRemove || "")
      .trim()
      .toLowerCase();
    if (!key) return;

    // remove from both sources
    setTempJobSkills((prev) => prev.filter((s) => s.toLowerCase() !== key));
    setApiJobSkills((prev) => prev.filter((s) => s.toLowerCase() !== key));

    // remember the removal so future API refreshes don't re-add it
    setRemovedJobSkills((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  // const handleAddNeededSkill = () => {
  //   // formData.hardSkills.skillsRequired
  //   if (
  //     newNeededSkill.trim() &&
  //     !formData.hardSkills.skillsRequired.includes(newNeededSkill.trim())
  //   ) {
  //     setFormData({
  //       ...formData,
  //       hardSkills: {
  //         ...formData.hardSkills,
  //         skillsRequired: [
  //           ...formData.hardSkills.skillsRequired,
  //           newNeededSkill.trim(),
  //         ],
  //       },
  //     });
  //     setNewNeededSkill("");
  //   }
  // };

  // const handleRemoveNeededSkill = (skillToRemove) => {
  //   setFormData({
  //     ...formData,
  //     hardSkills: {
  //       ...formData.hardSkills,
  //       skillsRequired: formData.hardSkills.skillsRequired.filter(
  //         (skill) => skill !== skillToRemove
  //       ),
  //     },
  //   });
  // };

  const [vmOpen, setVmOpen] = React.useState(false);
  const [vmTitle, setVmTitle] = React.useState("");
  const [vmSkills, setVmSkills] = React.useState([]);

  const openViewMore = (title, skills) => {
    setVmTitle(title);
    setVmSkills(skills || []);
    setVmOpen(true);
  };

  const handleAddPositionType = () => {
    if (
      newPositionType.trim() &&
      !formData.openPositionTypes.includes(newPositionType.trim())
    ) {
      setFormData({
        ...formData,
        openPositionTypes: [
          ...formData.openPositionTypes,
          newPositionType.trim(),
        ],
      });
      setNewPositionType("");
    }
  };

  const handleRemovePositionType = (positionToRemove) => {
    setFormData({
      ...formData,
      openPositionTypes: formData.openPositionTypes.filter(
        (position) => position !== positionToRemove,
      ),
    });
  };

  const handleModalClose = () => {
    setShowModal({ show: false, type: "", title: "", message: "" });
    // onNavigate("/team/id", { ...team, ...formData }); // This line was commented out in original
  };

  const getPerformanceColor = (performance) => {
    if (performance >= 90) return "performance-excellent";
    if (performance >= 80) return "performance-good";
    return "performance-average";
  };

  const [employees, setEmployees] = useState([]);
  const [employeesLength, setEmployeesLength] = useState(null);
  // Pagination for candidates
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20); // 20 items per page // Pagination
  const totalPages = Math.ceil(employeesLength / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };
  const paramId = useParams().id;
  useEffect(() => {
    getTeamByIdApiHandler(paramId);
    getDepartmentsListApiHandler();
    getTeamLeadsDropdownApiHandler();
  }, []); // keep employeesLength in sync (static list case)
  useEffect(() => {
    setEmployeesLength(employees.length || null);
    // if the list shrinks (e.g., after filter), clamp the page
    const newTotalPages = Math.max(
      1,
      Math.ceil(employees.length / itemsPerPage),
    );
    setCurrentPage((p) => Math.min(p, newTotalPages));
  }, [employees, itemsPerPage]);

  // slice for the current page
  const paginatedEmployees = useMemo(
    () => employees.slice(indexOfFirstItem, indexOfLastItem),
    [employees, indexOfFirstItem, indexOfLastItem],
  );

  const [teamData, setTeamData] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    department: "",
    lead: { userId: "", name: "" },
    description: "",
    workStyle: "",
    openPositions: 0,
    openPositionTypes: [],
    project: "",
    projects: [],
    skills: [], // General team skills
    neededSkills: [], // General needed skills
    teamCulture: "",
    softSkills: { benchmarkSkills: {} }, // New for soft skills
    hardSkills: { skillsRequired: [], skillsFound: [], skillsMissing: [] }, // New for hard skills
  });
  // merged view for rendering (prefer user override -> then API)
  const merged = {
    ...(formData.softSkills?.benchmarkSkills || {}), // baseline
    ...tempSoftSkills, // suggestions
    ...softSkillValues, // <-- ALWAYS WINS
  };
  const [allEmployees, setAllEmployees] = useState([]);
  const getTeamByIdApiHandler = async (id) => {
    try {
      const response = await getTeamByIdApi(id);
      console.log(
        "Fetched Team Data:",
        response.data.softSkills.benchmarkSkills,
      );
      setTeamData(response.data);
      setEmployees(response.data.matches || []);
      setAllEmployees(response.data.matches || []);

      setEmployeesLength(response.data.matches.length || null);
      setFormData({
        name: response.data.teamName || "",
        department: response.data.department || "",
        lead: {
          userId: response.data.teamLead?._id || "",
          name: response.data.teamLead?.name || "",
        },
        description: response.data.description || "",
        workStyle: response.data.workStyle || "",
        openPositions: response.data.openPositions || 0,
        openPositionTypes: response.data.openPositionTypes || [],
        skills: response.data.teamSkills || [], // General team skills
        neededSkills: response.data.neededSkills || [], // General needed skills
        teamCulture: response.data.teamCulture || "",
        // softSkills: response.data.softSkills.benchmarkSkills || {},
        hardSkills: response.data.hardSkills || {
          skillsRequired: [],
          skillsFound: [],
          skillsMissing: [],
        },
        projects: response.data.projects || [],
        softSkills: {
          benchmarkSkills: response.data.softSkills?.benchmarkSkills || {},
        },
      });

      // formData.softSkills.benchmarkSkills =
      //   response.data.softSkills.benchmarkSkills || {};
      // console.log("Fetched Soft Skills:", formData.softSkills.benchmarkSkills);

      console.log("Update Team Object:", formData.softSkills);

      setSelectedMembers(response.data.members);
      // Initialize soft skill values for editing
      // if (response.data?.softSkills?.benchmarkSkills) {
      //   setSoftSkillValues(response.data.softSkills.benchmarkSkills);
      // } else {
      //   console.log("No benchmark skills found");
      // }
    } catch (error) {
      console.error("Error fetching team data:", error);
      handleApiError(error, {
        showModal: false,
      });
    }
  };
  const showSkillsSpinner = loadingTab === "skills" && isNextLoading;
  const [departmentsList, setDepartmentsList] = useState([]);
  const getDepartmentsListApiHandler = async () => {
    try {
      const response = await getDepartmentsListApi();
      setDepartmentsList(response.data);
    } catch (error) {
      console.error("Error fetching departments list:", error);
      handleApiError(error, {
        showModal: false,
      });
    }
  };

  const [teamLead, setTeamLead] = useState([]);
  const getTeamLeadsDropdownApiHandler = async () => {
    try {
      const response = await getAllPeopleApi();
      setTeamLead(
        response.data.map((person) => ({
          name: person.name,
          id: person._id,
        })),
      );
    } catch (error) {
      handleApiError(error, {
        showModal: false,
      });
    }
  };

  // useEffect(() => {
  //   getEmployeesDropdownApiHandler();
  // }, [currentPage, searchTerm, filterDepartment]);

  // const getEmployeesDropdownApiHandler = async () => {
  //   try {
  //     let obj = {
  //       pageNo: currentPage,
  //       pageSize: itemsPerPage,
  //       searchFilter: searchTerm,
  //       department: filterDepartment === "All" ? "" : filterDepartment,
  //     };
  //     const response = await getAllPeopleApi(obj);
  //     setEmployees(response.data);
  //     setEmployeesLength(response.totalCount);
  //   } catch (error) {
  //     handleApiError(error, {
  //       showModal: false,
  //     });
  //   }
  // };
  const [isMemberSelected, setIsMemberSelected] = useState(null);
  const addMemberToTeamApiHandler = async (candidate) => {
    setIsMemberSelected(candidate._id);
    try {
      let userDetails = [
        {
          // userId: candidate.userId,
          userId: candidate._id,
          name: candidate.name,
        },
      ];
      let obj = {
        userDetails: userDetails,
        teamId: paramId,
      };
      const response = await addToTeamApi(obj);
      if (response.code === 200) {
        getTeamByIdApiHandler(paramId);
        setIsMemberSelected(null);
        setShowModal({
          show: true,
          type: "success",
          title: t("teamEditPage.messages.profileAddedTitle"),
          message: t("teamEditPage.messages.profileAddedMessage", {
            name: candidate.name,
          }),
          showButton: false,
          action: null,
          showLocalButton: false,
        });
      }
    } catch (error) {
      setIsMemberSelected(null);
      handleApiError(error, {
        showModal: true,
        showButton: false,
      });
    }
  };

  const handleAddMember = async (candidate) => {
    console.log("Adding member:", candidate);
    await getTeamByIdApiHandler(paramId);

    setShowModal({
      show: true,
      type: "info",
      title: t("teamEditPage.messages.addTeamMemberTitle"),
      message: t("teamEditPage.messages.addTeamMemberConfirm", {
        name: candidate.name,
        teamName: teamData.teamName,
      }),
      showLocalButton: true,
      action: () => {
        addMemberToTeamApiHandler(candidate);
      },
    });
  };

  const removeMemberFromTeamApiHandler = async (member) => {
    console.log("Removing member from team:", member);
    try {
      let userDetails = [
        {
          // userId: member.userId,
          userId: member._id,
          name: member.name,
        },
      ];
      let obj = {
        userDetails: userDetails,
        teamId: paramId,
      };
      const response = await removeFromTeamApi(obj);
      if (response.code === 200) {
        getTeamByIdApiHandler(paramId);
        setShowModal({
          show: true,
          type: "success",
          title: t("teamEditPage.messages.memberRemovedTitle"),
          message: t("teamEditPage.messages.memberRemovedMessage", {
            name: member.name,
            teamName: teamData.teamName,
          }),
          showButton: false,
          action: null,
          showLocalButton: false,
        });
      }
    } catch (error) {
      handleApiError(error, {
        showModal: true,
        showButton: false,
      });
    }
  };

  const handleRemoveMember = async (member) => {
    // await getTeamByIdApiHandler(paramId);
    // console.log(teamData, "teamData");

    setShowModal({
      show: true,
      type: "warning",
      title: t("teamEditPage.messages.removeTeamMemberTitle"),
      message: t("teamEditPage.messages.removeTeamMemberConfirm", {
        name: member.name,
      }),
      showLocalButton: true,
      action: () => {
        removeMemberFromTeamApiHandler(member);
      },
    });
  };

  // UPDATE TEAM DETAILS
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let obj = {
        teamId: paramId,
        teamName: formData.name,
        department: formData.department,
        teamLead: formData.lead,
        description: formData.description,
        workStyle: formData.workStyle,
        openPositions: formData.openPositions,
        teamCulture: formData.teamCulture,
        projects: formData.project,
        members: selectedMembers.map((mem) => {
          return {
            // userId: mem.userId,
            userId: mem._id,
            name: mem.name,
          };
        }),
        // neededSkills: formData.neededSkills,
        // softSkillsRequired: {
        //   ...softSkillValues, // Ensure updated soft skill values are sent
        // },
        softSkillsRequired: buildFinalSoftSkills(),
        jobSkillsRequired: finalNeededSkills,
        // jobSkillsRequired: formData.hardSkills.skillsRequired, // Assuming hard skills are updated via remove handler directly
        projects: formData.projects,
      };
      console.log("Update Team Object:", obj);
      const response = await updateTeamApi(obj);
      if (response.code === 200) {
        setShowModal({
          show: true,
          type: "success",
          title: t("teamEditPage.teamUpdated"),
          message: t("teamEditPage.teamUpdatedDetailsMessage", {
            teamName: formData.name,
          }),
        });
        // empty the temp
        setTempSoftSkills({});
        setTempJobSkills([]);
        setTimeout(() => {
          navigate(`/teams/${paramId}`);
        }, 1500);
      }
    } catch (error) {
      handleApiError(error, {
        showModal: true,
        showButton: false,
      });
    }
  };

  const getSkillsByTeamDescApiHandler = async (teamData) => {
    setIsNextLoading(true);
    try {
      const resp = await getSkillsByTeamDescApi({
        description: teamData.description,
        teamCulture: teamData.teamCulture,
      });

      const fetched = Object.fromEntries(
        Object.entries(resp?.data?.softSkillsRequired || {}).map(([k, v]) => [
          String(k),
          Number(v ?? 0),
        ]),
      );

      // 1) persist baseline for UI + submit
      setFormData((fd) => ({
        ...fd,
        softSkills: {
          ...fd.softSkills,
          benchmarkSkills: fetched,
        },
      }));

      // 2) top-up temp (don’t override user edits)
      setTempSoftSkills((prev) => {
        const next = { ...prev };
        for (const [name, value] of Object.entries(fetched)) {
          if (!(name in softSkillValues)) next[name] = value;
        }
        return next;
      });

      // (job skills as you had)
      const apiSkills = (resp?.data?.jobSkillsRequired ?? [])
        .map((s) => String(s || "").trim())
        .filter(Boolean);

      // don't reintroduce removed skills
      const removed = new Set(removedJobSkills);
      setApiJobSkills(apiSkills);

      if (resp?.code === 200) setActiveTab("skills");
    } catch (err) {
      handleApiError(err, { showModal: true });
    } finally {
      setIsNextLoading(false);
      setLoadingTab(null);
    }
  };

  // Build the final soft-skills object to send to API
  const buildFinalSoftSkills = () => {
    const base = formData.softSkills?.benchmarkSkills || {};
    const temp = tempSoftSkills || {};
    const edits = softSkillValues || {};

    // Union of all keys that exist anywhere
    const allKeys = new Set([
      ...Object.keys(base),
      ...Object.keys(temp),
      ...Object.keys(edits),
    ]);

    const clamp010 = (n) => Math.min(10, Math.max(0, n));

    const out = {};
    for (const name of allKeys) {
      // Prefer user edit if it’s a number; else temp; else base
      const raw =
        edits[name] !== "" && edits[name] !== undefined
          ? edits[name]
          : temp[name] !== undefined
            ? temp[name]
            : base[name];

      const n = Number(raw);
      out[name] = Number.isFinite(n) ? clamp010(n) : 0;
    }
    return out;
  };
  // Function to go to next tab
  const handleNextTab = () => {
    if (activeTab === "basic") {
      handleTabClick("skills");
      // getSkillsByTeamDescApiHandler(formData);
    } else if (activeTab === "members") {
      setActiveTab("members");
      handleSubmit(); // sets isUpdateLoading internally
    } else if (activeTab === "skills") {
      // handleTabClick("skills");
      setActiveTab("members");
    }
  };

  const handleTabClick = (tab) => {
    console.log(formData, "formData in tab click");
    if (tab === "basic") {
      setActiveTab("basic");
      return;
    }

    if (tab === "skills") {
      if (
        !formData.name ||
        !formData.department ||
        !formData.description ||
        !formData.teamCulture ||
        !formData.lead.userId
      ) {
        setShowModal({
          show: true,
          type: "error",
          title: t("teamEditPage.messages.invalidFormTitle"),
          message: t("teamEditPage.messages.fillRequiredFields"),
          showButton: false,
        });
        return;
      }

      // stay on current tab, but mark the Skills tab as loading
      setLoadingTab("skills");
      getSkillsByTeamDescApiHandler(formData);
      return;
    }

    if (tab === "members") {
      buildFinalSoftSkills();
      setActiveTab("members");
    }
  };

  // helper
  const safeIncludes = (val = "", term = "") =>
    String(val).toLowerCase().includes(term);

  // re-filter whenever inputs change
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    const dept = (filterDepartment || "").trim(); // "" means all

    const filtered = allEmployees.filter((e) => {
      const matchesSearch =
        !term ||
        safeIncludes(e.name, term) ||
        safeIncludes(e.designation, term) || // ← use designation (what you render)
        safeIncludes(e.role, term) ||
        (Array.isArray(e.skills) &&
          e.skills.some((s) => safeIncludes(s, term)));

      const matchesDepartment =
        !dept || dept === "All" || e.department === dept;

      return matchesSearch && matchesDepartment;
    });

    setEmployees(filtered);
    setEmployeesLength(filtered.length || null);
    setCurrentPage(1);
  }, [searchTerm, filterDepartment]);
  const cancelButtonHandler = async () => {
    navigate(`/teams/${paramId}`);
  };

  // Handlers for soft skills
  // const handleSoftSkillValueChange = (skillName, value) => {
  //   setSoftSkillValues((prev) => ({
  //     ...prev,
  //     [skillName]: parseInt(value) || 0,
  //   }));
  // };

  const handleRemoveProjects = (projectToRemove) => {
    setFormData({
      ...formData,
      projects: formData.projects.filter(
        (project) => project !== projectToRemove,
      ),
    });
  };

  const handleProjects = (e) => {
    // 2) Enter handler: add to list + clear input

    if (e.key !== "Enter") return;
    e.preventDefault(); // avoid form submit

    const value = (e.target.value || "").trim();
    if (!value) return;

    setFormData((prev) => {
      // avoid duplicates (optional)
      if (prev.projects?.includes(value)) {
        return { ...prev, project: "" }; // just clear input
      }
      return {
        ...prev,
        projects: [...(prev.projects || []), value],
        project: "", // clear the input
      };
    });
  };
  // const handleSoftSkillValueChange = (skillName, value) => {
  //   if (value === "" || (Number(value) >= 0 && Number(value) <= 10)) {
  //     setSoftSkillValues((prev) => ({
  //       ...prev,
  //       [skillName]: value,
  //     }));
  //   }
  // };

  const handleSoftSkillValueChange = (skillName, value) => {
    const n = value === "" ? "" : Number(value);
    if (value === "" || (!Number.isNaN(n) && n >= 0 && n <= 10)) {
      setSoftSkillValues((prev) => {
        const next = { ...prev };
        next[skillName] = n;
        return next;
      });
      setTempSoftSkills((prev) => {
        const next = { ...prev };
        next[skillName] = n;
        return next;
      });
    }
  };
  // useEffect(() => {
  //   if (formData?.softSkills?.benchmarkSkills) {
  //     setSoftSkillValues(formData?.softSkills?.benchmarkSkills);
  //   }
  // }, [formData?.softSkills?.benchmarkSkills]);

  const handleEditSoftSkill = (skillName, value) => {
    setEditingSoftSkill({ skillName, originalValue: value });
  };

  const handleSaveSoftSkill = () => {
    setEditingSoftSkill(null);
    // In a real app, you'd send softSkillValues to your backend here
    console.log("Soft skill values saved:", softSkillValues);
  };

  const handleCancelEditSoftSkill = () => {
    if (editingSoftSkill) {
      setSoftSkillValues((prev) => ({
        ...prev,
        [editingSoftSkill.skillName]: editingSoftSkill.originalValue,
      }));
    }
    setEditingSoftSkill(null);
  };

  // Handlers for job skills
  const handleRemoveJobSkill = (skillToRemove) => {
    const updatedSkills = formData.hardSkills.skillsFound.filter(
      (skill) => skill !== skillToRemove,
    );
    setFormData((prev) => ({
      ...prev,
      hardSkills: {
        ...prev.hardSkills,
        skillsFound: updatedSkills,
      },
    }));
    // Close modal if the skill was removed from the modal list
    if (showJobSkillListModal.visible && showJobSkillListModal.removable) {
      setShowJobSkillListModal((prev) => ({
        ...prev,
        skills: updatedSkills,
      }));
    }
    console.log("Job skill removed:", skillToRemove);
  };

  // Helper to render skill sections with optional "more" button and modal
  const renderSkillSection = ({
    title,
    skills,
    type,
    limit = 10,
    isEditable = false,
    isRemovable = false,
  }) => {
    const displayedSkills = skills.slice(0, limit);
    const remainingCount = skills.length - limit;

    const openModal = () => {
      if (type === "soft") {
        const softSkillsWithValues = skills.map((skillName) => ({
          name: skillName,
          value:
            softSkillValues[skillName] !== undefined
              ? softSkillValues[skillName]
              : t("teamEditPage.notAvailableShort"),
        }));
        setShowSoftSkillListModal({
          visible: true,
          title: t("teamEditPage.allTitle", {
            title,
          }),
          skills: softSkillsWithValues,
          type: "soft",
        });
      } else {
        // type === 'job'
        setShowJobSkillListModal({
          visible: true,
          title: t("teamEditPage.allTitle", {
            title,
          }),
          skills: skills,
          type: "job",
          removable: isRemovable,
        });
      }
    };

    return (
      <div className="skill-section-container">
        <h4>
          {title} ({skills.length})
        </h4>
        <div
          className={type === "soft" ? "soft-skills-grid" : "job-skills-list"}
        >
          {displayedSkills.length === 0 ? (
            <span className="no-skills-message">
              {t("common.noSkillsFound")}
            </span>
          ) : (
            displayedSkills.map((skill, index) => {
              if (type === "soft") {
                const skillName = skill;
                const value = softSkillValues[skillName];
                return (
                  <div key={index} className="soft-skill-item">
                    <div className="soft-skill-info">
                      <span className="soft-skill-name">{skillName}:</span>
                      {isEditable &&
                      editingSoftSkill?.skillName === skillName ? (
                        <div className="soft-skill-value-edit">
                          <input
                            type="number"
                            value={value}
                            onChange={(e) =>
                              handleSoftSkillValueChange(
                                skillName,
                                e.target.value,
                              )
                            }
                            onBlur={handleSaveSoftSkill}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveSoftSkill();
                              if (e.key === "Escape")
                                handleCancelEditSoftSkill();
                            }}
                          />
                          <div className="soft-skill-edit-actions">
                            <button
                              onClick={handleSaveSoftSkill}
                              className="soft-skill-save-btn"
                            >
                              {t("common.save")}
                            </button>
                            <button
                              onClick={handleCancelEditSoftSkill}
                              className="soft-skill-cancel-btn"
                            >
                              {t("common.cancel")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="soft-skill-display">
                          <span className="soft-skill-value">
                            {value !== undefined
                              ? value
                              : t("teamEditPage.notAvailableShort")}
                          </span>
                          {isEditable && (
                            <button
                              onClick={() =>
                                handleEditSoftSkill(skillName, value)
                              }
                              className="soft-skill-edit-btn"
                            >
                              <Pencil size={14} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              } else {
                // type === 'job'
                return (
                  <div key={index} className="job-skill-card">
                    <span>{skill}</span>
                    {isRemovable && (
                      <button
                        onClick={() => handleRemoveJobSkill(skill)}
                        className="job-skill-remove-btn"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                );
              }
            })
          )}
          {remainingCount > 0 && (
            <button onClick={openModal} className="view-more-skills-btn">
              {t("teamEditPage.moreCount", {
                count: remainingCount,
              })}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="team-edit-page">
      <button
        className="nav-back"
        onClick={() => window.history.back()}
        style={{
          position: "absolute",
          top: "100px",
          left: "20px",
          color: "#333",
        }}
      >
        <ArrowLeft size={16} />
        {t("common.back")}
      </button>

      <main className="team-edit-main">
        <div className="container">
          <div className="page-header">
            <div className="header-icon">
              <Edit3 size={32} />
            </div>
            <h1>
              {t("teamEditPage.pageTitleWithName", {
                teamName: teamData?.teamName,
              })}
            </h1>
            <p>{t("teamEditPage.pageDescriptionCurrent")}</p>
          </div>

          {/* Tab Navigation */}
          {/* <div className="tab-navigation-edit">
            <button
              className={`tab-btn-edit ${
                activeTab === "basic" ? "active" : ""
              }`}
              onClick={() => setActiveTab("basic")}
            >
              <Settings size={16} />
              Basic Information
            </button>

            <button
              className={`tab-btn-edit ${
                activeTab === "skills" ? "active" : ""
              }`}
              onClick={() => setActiveTab("skills")}
            >
              <Target size={16} />
              Skills & Requirements
            </button>
            <button
              className={`tab-btn-edit ${
                activeTab === "members" ? "active" : ""
              }`}
              onClick={() => setActiveTab("members")}
            >
              <Users size={16} />
              Add Members
            </button>
          </div> */}

          <div className="tab-navigation">
            {/* TEAM INFO */}
            <button
              // disabled={activeTab === "members"}
              className={`tab-btn ${activeTab === "basic" ? "active" : ""}`}
              onClick={() => handleTabClick("basic")}
            >
              <Settings size={16} />
              {t("teamEditPage.basicInfo")}
            </button>
            {/* SKILLS */}
            {/* <button
      
              className={`tab-btn ${activeTab === "skills" ? "active" : ""}`}
              onClick={() => handleTabClick("skills")}
            >
     
              {(activeTab === "basic" && isNextLoading) ||
              (activeTab === "members" && isUpdateLoading) ? (
                <>
                  <Loader2 size={16} className="spin" />
                </>
              ) : (
                <>
                  <Target size={16} />
                </>
              )}
              Skills & requirements
            </button> */}
            <button
              className={`tab-btn ${activeTab === "skills" ? "active" : ""}`}
              onClick={() => handleTabClick("skills")}
              disabled={showSkillsSpinner} // prevent double-click spam
              aria-busy={showSkillsSpinner}
            >
              {showSkillsSpinner ? (
                <Loader2 size={16} className="spin" />
              ) : (
                <Target size={16} />
              )}
              {t("teamEditPage.skillsRequirementsLower")}
            </button>

            {/* MEMBERS */}
            <button
              className={`tab-btn ${activeTab === "members" ? "active" : ""}`}
              onClick={() => handleTabClick("members")}
            >
              <Users size={16} />
              {t("teamEditPage.addMembersLower")}
            </button>
          </div>

          <form className="team-edit-form">
            {activeTab === "members" && (
              <div className="form-section">
                <h2>
                  <Users size={20} />
                  {t("teamEditPage.teamMembers")}
                </h2>

                {/* New section for Open Positions */}
                {/* <div className="open-positions-note">
                  <p>
                    First, add members to your team. After that, you can specify
                    the number and types of open positions.
                  </p>
                  <div className="form-group">
                    <label>Open positions</label>
                    <div className="number-input">
                      <button
                        type="button"
                        className="number-btn"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            openPositions: Math.max(
                              0,
                              formData.openPositions - 1
                            ),
                          })
                        }
                      >
                        <Minus size={16} />
                      </button>
                      <input
                        type="number"
                        value={formData.openPositions}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            openPositions: Number.parseInt(e.target.value) || 0,
                          })
                        }
                        min="0"
                        className="number-field"
                      />
                      <button
                        type="button"
                        className="number-btn"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            openPositions: formData.openPositions + 1,
                          })
                        }
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                </div> */}

                {/* Current Team Members */}
                {selectedMembers.length > 0 && (
                  <div className="current-members-edit">
                    <h3>
                      {t("teamEditPage.currentMembers", {
                        count: selectedMembers.length,
                      })}
                    </h3>
                    <div className="current-members-grid">
                      {selectedMembers.map((member) => (
                        <div key={member.id} className="current-member-card">
                          <div className="member-info-edit">
                            <div className="member-avatar-edit">
                              <User size={16} />
                            </div>
                            <div>
                              <h4>{member.name}</h4>
                              <p>{member.designation}</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="remove-member-btn-edit"
                            onClick={() => handleRemoveMember(member)}
                            title={t("teamEditPage.removeFromTeam")}
                          >
                            <Minus size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available Employees */}
                <div className="available-employees-edit">
                  <div className="header-edit">
                    <h3>{t("teamEditPage.addNewMembers")}</h3>
                  </div>

                  <div className="employee-filters-edit">
                    <div className="search-container-edit">
                      <Search size={20} className="search-icon-edit" />
                      <input
                        type="text"
                        placeholder={t("teamEditPage.searchEmployees")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input-edit"
                      />
                    </div>

                    <div className="filter-group-edit">
                      <Filter size={16} />
                      <label>{t("teamEditPage.departmentFilter")}</label>
                      <select
                        value={filterDepartment}
                        onChange={(e) => setFilterDepartment(e.target.value)}
                        className="filter-select-edit"
                      >
                        <option value="All">{t("common.all")}</option>
                        {departmentsList.map((dept) => (
                          <option key={dept._id} value={dept.name}>
                            {dept.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {paginatedEmployees.length === 0 ? (
                    <NoDataFound />
                  ) : (
                    <div className="available-employees-grid-edit">
                      {paginatedEmployees?.map((employee) => (
                        <div
                          key={employee?._id}
                          className="available-employee-card-edit"
                        >
                          <div className="employee-card-header-edit">
                            <div className="employee-info-edit">
                              <div className="employee-avatar-edit">
                                <User size={16} />
                              </div>
                              <div>
                                <h4>{employee?.name}</h4>
                                <p>{employee?.designation}</p>
                                <p className="employee-department-edit">
                                  {employee?.department}
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="add-member-btn-edit"
                              onClick={() => handleAddMember(employee)}
                              title={t("teamEditPage.addToTeam")}
                            >
                              {/* {loadingPersonId === (employee.userId ?? employee._id)  */}
                              {isMemberSelected === employee._id ? (
                                <>
                                  <Check size={16} />
                                </>
                              ) : (
                                <>
                                  <Plus size={16} />
                                </>
                              )}
                            </button>
                          </div>

                          <div className="employee-card-details-edit">
                            <div className="detail-row-edit">
                              <div
                                className={`performance-score-edit ${getPerformanceColor(
                                  employee?.teamFit,
                                )}`}
                                style={{ display: "flex" }}
                              >
                                {t("teamEditPage.teamFit")} {employee?.teamFit}%
                                <div className="mobility-tooltip">
                                  <Info
                                    size={14}
                                    className="mobility-score-info-icon"
                                    aria-describedby={`exp-tip-${
                                      employee.userId || employee.id || "x"
                                    }`}
                                    tabIndex={0}
                                    onMouseEnter={decideTooltipSide}
                                    onFocus={decideTooltipSide}
                                  />
                                  <div
                                    className="mobility-score-hover-box"
                                    role="tooltip"
                                  >
                                    <Explainability
                                      text={employee.explainability}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="employee-skills-edit">
                              <h5>{t("teamEditPage.skillsLabel")}</h5>
                              <div className="skills-tags-edit">
                                {(employee?.skills || [])
                                  .slice(0, 5)
                                  .map((skill, index) => (
                                    <span
                                      key={index}
                                      className="skill-tag-small-edit"
                                    >
                                      {typeof skill === "string"
                                        ? skill
                                        : skill?.name}
                                    </span>
                                  ))}

                                {(employee?.skills?.length || 0) > 5 && (
                                  <button
                                    type="button"
                                    className="view-more-skills-btn"
                                    onClick={() =>
                                      openViewMore(
                                        t("teamEditPage.allSkillsFor", {
                                          name: employee?.name,
                                        }),
                                        employee.skills,
                                      )
                                    }
                                    style={{ fontSize: "12px" }}
                                  >
                                    {t("teamEditPage.moreCount", {
                                      count: employee.skills.length - 5,
                                    })}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Pagination */}
                  {employeesLength && (
                    <div className="pagination-container">
                      <div className="pagination-info">
                        {t("teamEditPage.paginationInfo", {
                          start: indexOfFirstItem + 1,
                          end: Math.min(indexOfLastItem, employeesLength),
                          total: employeesLength,
                        })}
                      </div>
                      <div className="pagination-controls">
                        <button
                          type="button"
                          className="pagination-btn"
                          onClick={handlePrevPage}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft />
                        </button>
                        {Array.from(
                          { length: totalPages },
                          (_, i) => i + 1,
                        ).map((pageNumber) => (
                          <button
                            type="button"
                            key={pageNumber}
                            className={`pagination-btn ${
                              currentPage === pageNumber ? "active" : ""
                            }`}
                            onClick={() => handlePageChange(pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        ))}
                        <button
                          type="button"
                          className="pagination-btn"
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                        >
                          <ChevronRight />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "skills" && (
              <div className="form-section">
                <h2>
                  <Target size={20} />
                  {t("teamEditPage.skillsRequirements")}
                </h2>

                {/* General Team Skills (read-only as per previous instruction) */}
                {/*<div className="skill-section-container">
                  <h4>Current Team Skills ({formData.skills.length})</h4>
                  <div className="skills-tags">
                    {formData.skills.length === 0 ? (
                      <span className="no-skills-message">No skills found.</span>
                    ) : (
                      formData.skills.map((skill, index) => (
                        <span key={index} className="skill-tag">
                          {skill}
                        </span>
                      ))
                    )}
                  </div>
                </div>*/}

                {/* Soft Skills Section */}
                {/* <div className="soft-skills-list">
                  <h4>Required Soft Skills</h4>
                  <div className="soft-skills-grid">
                    {formData?.softSkills?.benchmarkSkills &&
                      Object.keys(
                        formData?.softSkills?.benchmarkSkills || {}
                      ).map((skill, index) => (
                        <div key={index} className="soft-skill-item">
                          <p className="soft-skill-info">
                            {skill}
                            <input
                              type="number"
                              min={0}
                              max={10}
                              style={{
                                width: "50px",
                                height: "25px",
                                padding: "0 5px",
                              }}
                              value={softSkillValues[skill] ?? ""}
                              onChange={(e) =>
                                handleSoftSkillValueChange(
                                  skill,
                                  e.target.value
                                )
                              }
                            />
                            /10
                          </p>
                        </div>
                      ))}
                  </div>
                </div> */}

                {/* <div className="skill-gaps-section">
                  <h4>Required Soft Skills</h4>
                  <div className="soft-skills-grid">
                    {Object.keys(
                      formData?.softSkills?.benchmarkSkills || {}
                    ).map((skill, index) => (
                      <div key={index} className="soft-skill-item">
                        <div className="soft-skill-info">
                          <span className="soft-skill-name">{skill}</span>
                          <label
                            className="rating-field"
                            aria-label={`${skill} rating out of 10`}
                          >
                            <input
                              type="number"
                              min={0}
                              max={10}
                              step={1}
                              inputMode="numeric"
                              className="rating-input"
                              // value={softSkillValues[skill] ?? ""}
                              value={
                                softSkillValues[skill] ??
                                tempSoftSkills[skill] ??
                                ""
                              }
                              onChange={(e) =>
                                handleSoftSkillValueChange(
                                  skill,
                                  e.target.value
                                )
                              }
                            />
                            <span className="rating-suffix">/10</span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div> */}
                <div className="skill-gaps-section">
                  <h4>{t("teamEditPage.requiredSoftSkills")}</h4>

                  {Object.keys(merged).length === 0 ? (
                    <span className="no-skills-message">
                      {t("teamEditPage.addDescriptionAndCultureForSoftSkills")}
                    </span>
                  ) : (
                    <div className="soft-skills-grid">
                      {Object.keys(merged).map((skill) => (
                        <div key={skill} className="soft-skill-item">
                          <div className="soft-skill-info">
                            <span className="soft-skill-name">{skill}</span>
                            <label
                              className="rating-field"
                              aria-label={t("teamEditPage.ratingOutOfTen", {
                                skill,
                              })}
                            >
                              <input
                                type="number"
                                min={0}
                                max={10}
                                step={1}
                                inputMode="numeric"
                                className="rating-input"
                                value={
                                  softSkillValues[skill] ??
                                  tempSoftSkills[skill] ??
                                  ""
                                }
                                onChange={(e) =>
                                  handleSoftSkillValueChange(
                                    skill,
                                    e.target.value,
                                  )
                                }
                              />
                              <span className="rating-suffix">/10</span>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="skill-gaps-section">
                  {/* <h4>Soft Skills</h4> */}
                  {/* {renderSkillSection({
                    title: "Required Soft Skills",
                    skills: Object.keys(
                      formData.softSkills.benchmarkSkills || {}
                    ),
                    type: "soft",
                    isEditable: true,
                  })} */}
                  {/* {renderSkillSection({
                    title: "Current Soft Skills",
                    skills: formData.softSkills.skillsFound || [],
                    type: "soft",
                    isEditable: true,
                  })} */}
                  {/* {renderSkillSection({
                    title: "Missing Soft Skills",
                    skills: formData.softSkills.skillsMissing || [],
                    type: "soft",
                    isEditable: false,
                  })} */}
                </div>

                {/* Job Skills Section */}
                <div className="skill-gaps-section">
                  {/* <h4>Job Skills</h4> */}
                  {/* {renderSkillSection({
                    title: "Required Job Skills",
                    skills: formData.hardSkills.skillsRequired || [],
                    type: "job",
                    isRemovable: false,
                  })} */}
                  {/* {renderSkillSection({
                    title: "Current Job Skills",
                    skills: formData.hardSkills.skillsFound || [],
                    type: "job",
                    isRemovable: true,
                  })} */}
                  {/* {renderSkillSection({
                    title: "Missing Job Skills",
                    skills: formData.hardSkills.skillsMissing || [],
                    type: "job",
                    isRemovable: false,
                  })} */}
                  <h4>
                    {t("teamEditPage.requiredJobSkillsWithCount", {
                      count: combinedJobSkills.length,
                    })}
                  </h4>

                  <div className="skill-input-group">
                    <input
                      type="text"
                      value={newNeededSkill}
                      onChange={(e) => setNewNeededSkill(e.target.value)}
                      placeholder={t("teamEditPage.addNeededSkill")}
                      onKeyDown={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleAddNeededSkill())
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={handleAddNeededSkill}
                    >
                      <Plus size={14} />
                      {t("common.add")}
                    </button>
                  </div>

                  <div className="skills-tags">
                    {combinedJobSkills.length === 0 ? (
                      <span className="no-skills-message">
                        {t("common.noSkillsFound")}
                      </span>
                    ) : (
                      combinedJobSkills.map((item, index) => (
                        <span
                          key={`${item.label}-${index}`}
                          className="needed-tag"
                        >
                          {item.label}
                          {/* Remove only affects user-added items; hide the X for pure-API suggestions if you want */}
                          <button
                            type="button"
                            className="remove-skill"
                            onClick={() => handleRemoveNeededSkill(item.label)}
                            title={t("common.remove")}
                          >
                            <Minus size={12} />
                          </button>
                          {/* Optional: show a tiny badge if this came from API only */}
                          {/* {!item.fromUser && item.fromApi && <em className="badge">suggested</em>} */}
                        </span>
                      ))
                    )}
                  </div>
                </div>

                {/* Needed Skills (General) */}
                {/* <div className="skill-section-container"> */}
                {/* <h4>Needed Skills ({formData.neededSkills.length})</h4> */}
                {/* <div className="skill-input-group">
                    <input
                      type="text"
                      value={newNeededSkill}
                      onChange={(e) => setNewNeededSkill(e.target.value)}
                      placeholder="Add a needed skill..."
                      onKeyPress={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleAddNeededSkill())
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={handleAddNeededSkill}
                    >
                      <Plus size={14} />
                      Add
                    </button>
                  </div>
                  <div className="skills-tags">
                    {formData.neededSkills.length === 0 ? (
                      <span className="no-skills-message">
                        No skills found.
                      </span>
                    ) : (
                      // formData.neededSkills.map((skill, index) => (
                      formData.hardSkills.skillsRequired.map((skill, index) => (
                        <span key={index} className="needed-tag">
                          {skill}
                          <button
                            type="button"
                            className="remove-skill"
                            onClick={() => handleRemoveNeededSkill(skill)}
                          >
                            <Minus size={12} />
                          </button>
                        </span>
                      ))
                    )}
                  </div> */}
                {/* </div> */}
              </div>
            )}

            {activeTab === "basic" && (
              <div className="form-section">
                <h2>
                  <Settings size={20} />
                  {t("teamEditPage.basicInformation")}
                </h2>

                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      {t("teamEditPage.teamName")}{" "}
                      <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                      placeholder={t("teamEditPage.enterTeamName")}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      {t("teamEditPage.department")}{" "}
                      <span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      placeholder={t("teamEditPage.selectDepartment")}
                      value={formData.department}
                      onChange={(e) =>
                        setFormData({ ...formData, department: e.target.value })
                      }
                      required
                    >
                      <option value="">
                        {t("teamEditPage.selectDepartment")}
                      </option>
                      {departmentsList.map((dept) => (
                        <option key={dept._id} value={dept.name}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>
                      {t("teamEditPage.teamLead")}{" "}
                      <span style={{ color: "red" }}>*</span>
                    </label>

                    <select
                      value={formData.lead.userId || ""} // ✅ correct binding
                      onChange={(e) => {
                        const selectedLead = teamLead.find(
                          (lead) => lead.id === e.target.value,
                        );

                        setFormData((prev) => ({
                          ...prev,
                          lead: selectedLead
                            ? {
                                userId: selectedLead.id,
                                name: selectedLead.name,
                              } // ✅ store selected _id
                            : { userId: "", name: "" },
                        }));
                      }}
                      required
                    >
                      <option value="">
                        {t("teamEditPage.selectTeamLead")}
                      </option>
                      {teamLead.map((lead) => (
                        <option key={lead.id} value={lead.id}>
                          {lead.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t("teamEditPage.workStyle")}</label>
                    <select
                      value={formData.workStyle}
                      onChange={(e) =>
                        setFormData({ ...formData, workStyle: e.target.value })
                      }
                    >
                      <option value="">
                        {t("teamEditPage.selectWorkStyle")}
                      </option>
                      {workStyles.map((style) => (
                        <option key={style} value={style}>
                          {t(
                            `teamEditPage.workStyles.${style
                              .toLowerCase()
                              .replace("-", "")}`,
                          )}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* <div className="form-group">
                    <label>Open Positions</label>
                    <div className="number-input">
                      <button
                        type="button"
                        className="number-btn"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            openPositions: Math.max(
                              0,
                              formData.openPositions - 1
                            ),
                          })
                        }
                      >
                        <Minus size={16} />
                      </button>
                      <input
                        type="number"
                        value={formData.openPositions}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            openPositions: Number.parseInt(e.target.value) || 0,
                          })
                        }
                        min="0"
                        className="number-field"
                      />
                      <button
                        type="button"
                        className="number-btn"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            openPositions: formData.openPositions + 1,
                          })
                        }
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div> */}

                  <div className="form-group">
                    <label>{t("teamEditPage.projects")}</label>
                    <input
                      type="text"
                      value={formData.project}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          project: e.target.value,
                        }))
                      }
                      onKeyDown={handleProjects}
                      placeholder={t("teamEditPage.enterProjectName")}
                    />

                    <div className="skills-tags">
                      {!formData.projects ? (
                        <span className="no-skills-message">
                          {t("teamEditPage.noProjectsFound")}
                        </span>
                      ) : (
                        formData.projects?.map((project, index) => (
                          <span key={index} className="needed-tag">
                            {project}
                            <button
                              type="button"
                              className="remove-skill"
                              onClick={() => handleRemoveProjects(project)}
                            >
                              <Minus size={12} />
                            </button>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>
                    {t("teamEditPage.description")}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    required
                    placeholder={t("teamEditPage.enterDescription")}
                    rows={3}
                  />
                </div>

                <div className="form-group full-width">
                  <label>
                    {t("teamEditPage.teamCulture")}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <textarea
                    value={formData.teamCulture}
                    onChange={(e) =>
                      setFormData({ ...formData, teamCulture: e.target.value })
                    }
                    placeholder={t("teamEditPage.enterTeamCulture")}
                    rows={2}
                  />
                </div>
              </div>
            )}

            {/* <div className="form-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => onNavigate("/teams")}
              >
                {t("teamEditPage.cancel")}
              </button>

              {activeTab === "members" ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                >
                  <Save size={16} />
                  Save Changes
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleNextTab}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              )}
            </div> */}

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => onNavigate("/teams")}
                disabled={isNextLoading || isUpdateLoading}
              >
                {t("common.cancel")}
              </button>

              {activeTab === "members" ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                >
                  {t("teamEditPage.updateTeam")}
                  <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleNextTab}
                  disabled={
                    (activeTab === "basic" && isNextLoading) ||
                    (activeTab === "members" && isUpdateLoading)
                  }
                >
                  {(activeTab === "basic" && isNextLoading) ||
                  (activeTab === "members" && isUpdateLoading) ? (
                    <>
                      <Loader2 size={16} className="spin" />
                      {activeTab === "members"
                        ? t("teamEditPage.addingMembers")
                        : t("common.loading")}
                    </>
                  ) : (
                    <>
                      {activeTab === "members"
                        ? t("teamEditPage.addMembersLower")
                        : t("common.next")}
                      <ChevronRight size={16} />
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </main>

      {/* Soft Skills List Modal */}
      {showSoftSkillListModal.visible && (
        <SkillListModal
          title={showSoftSkillListModal.title}
          skills={showSoftSkillListModal.skills}
          type="soft"
          onClose={() =>
            setShowSoftSkillListModal({
              visible: false,
              title: "",
              skills: [],
              type: "soft",
            })
          }
        />
      )}

      {/* Job Skills List Modal */}
      {showJobSkillListModal.visible && (
        <SkillListModal
          title={showJobSkillListModal.title}
          skills={showJobSkillListModal.skills}
          type="job"
          onClose={() =>
            setShowJobSkillListModal({
              visible: false,
              title: "",
              skills: [],
              type: "job",
              removable: false,
            })
          }
          onRemoveSkill={
            showJobSkillListModal.removable ? handleRemoveJobSkill : undefined
          }
        />
      )}

      <Modal
        show={showModal.show}
        onClose={() =>
          setShowModal({
            show: false,
            type: "",
            title: "",
            message: "",
            action: null,
          })
        }
        type={showModal.type}
        title={showModal.title}
        message={showModal.message}
        showButton={false}
      >
        {showModal.showLocalButton && (
          <div className="modal-actions">
            <button
              className="btn btn-outline"
              onClick={() =>
                setShowModal({
                  show: false,
                  type: "",
                  title: "",
                  message: "",
                  action: null,
                })
              }
            >
              {t("common.cancel")}
            </button>
            <button className="btn btn-primary" onClick={showModal.action}>
              {t("common.confirm")}
            </button>
          </div>
        )}
      </Modal>
      <ViewMoreModal
        open={vmOpen}
        onClose={() => setVmOpen(false)}
        title={vmTitle}
        skills={vmSkills}
        variant="job-skills" // uses your modal CSS/chips
        type="hard" // treat as simple list; 'soft' shows values if present
      />

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />
    </div>
  );
};

export default TeamEditPage;
